import {useNeuroState} from "@ignitesales/neuro-application";

/**
 * @typedef ThemeSettingsHookValues
 * @property {function(key:string, groupKey?:string|null): any} getThemeConfigValue - returns value for a theme setting config based on key (can also filter by group key), null if not found
 * @property {string|null} landingPageType - returns landing pageType, if configured. Else, null
 * @property string|null surveyPageType - returns survey pageType, if configured. Else, null
 * @property string|null summaryPageType - returns summary pageType, if configured. Else, null
 * @property string|null detailsPageType - returns details pageType, if configured. Else, null
 * @property {function(): string} getGeneralThemeConfigs - returns default group the theme settings
 * @property {function(): string} getLandingThemeConfigs - returns landing group theme settings
 * @property {function(): string} getSurveyThemeConfigs - returns survey group theme settings
 * @property {function(): string} getSummaryThemeConfigs - returns summary group theme settings
 * @property {function(): string} getDetailsThemeConfigs - returns details group theme settings
 * @property {function(groupKey: string): Object} getThemeGroupValues - returns theme settings based on group key.
 * @property {Object} themeSettings - full theme settings with only the values in each group
 * @property {String} themeName - name / key of the theme being used
 */

/**
 * Theme settings, based on the manifest for the respective guide
 * @returns {ThemeSettingsHookValues}
 */
const useThemeSettings = () => {
    const { themeSettings, themeName } = useNeuroState();

    const getThemeConfigValue = (key, groupKey) => {
        let result = null;
        Object.keys(themeSettings)
            .filter(grpKey => !groupKey || grpKey === groupKey)
            .forEach(grpKey => {
                const groupConfigs = themeSettings[grpKey];
                if(groupConfigs.hasOwnProperty(key)) {
                    result = groupConfigs[key];
                }
            });
        return result;
    };

    const getThemeGroupValues = groupKey => {
        return themeSettings.hasOwnProperty(groupKey) ? themeSettings[groupKey] : {};
    };

    const getGeneralThemeConfigs = () => getThemeGroupValues(THEME_SETTINGS_CONSTANTS.GROUP_KEY_GENERAL);

    const landingPageType = getThemeConfigValue(THEME_SETTINGS_CONSTANTS.PAGE_TYPE_KEY, THEME_SETTINGS_CONSTANTS.GROUP_KEY_LANDING);
    const getLandingThemeConfigs = () => getThemeGroupValues(THEME_SETTINGS_CONSTANTS.GROUP_KEY_LANDING);

    const surveyPageType = getThemeConfigValue(THEME_SETTINGS_CONSTANTS.PAGE_TYPE_KEY, THEME_SETTINGS_CONSTANTS.GROUP_KEY_SURVEY);
    const getSurveyThemeConfigs = () => getThemeGroupValues(THEME_SETTINGS_CONSTANTS.GROUP_KEY_SURVEY);

    const summaryPageType = getThemeConfigValue(THEME_SETTINGS_CONSTANTS.PAGE_TYPE_KEY, THEME_SETTINGS_CONSTANTS.GROUP_KEY_SUMMARY);
    const getSummaryThemeConfigs = () => getThemeGroupValues(THEME_SETTINGS_CONSTANTS.GROUP_KEY_SUMMARY);

    const detailsPageType = getThemeConfigValue(THEME_SETTINGS_CONSTANTS.PAGE_TYPE_KEY, THEME_SETTINGS_CONSTANTS.GROUP_KEY_DETAILS);
    const getDetailsThemeConfigs = () => getThemeGroupValues(THEME_SETTINGS_CONSTANTS.GROUP_KEY_DETAILS);

    /**
     * @returns {BlockConfig}
     */
    const getBlocksThemeConfigs = () => getThemeGroupValues(THEME_SETTINGS_CONSTANTS.GROUP_KEY_BLOCKS);

    return {
        getThemeConfigValue,
        getThemeGroupValues,
        themeSettings,
        themeName,
        landingPageType,
        surveyPageType,
        summaryPageType,
        detailsPageType,
        getGeneralThemeConfigs,
        getLandingThemeConfigs,
        getSurveyThemeConfigs,
        getSummaryThemeConfigs,
        getDetailsThemeConfigs,
        getBlocksThemeConfigs
    };
};

const THEME_SETTINGS_CONSTANTS = {
    PAGE_TYPE_KEY: 'pageTypes',
    GROUP_KEY_GENERAL: 'general',
    GROUP_KEY_LANDING: 'landing',
    GROUP_KEY_SURVEY: 'survey',
    GROUP_KEY_SUMMARY: 'summary',
    GROUP_KEY_DETAILS: 'details',
    GROUP_KEY_BLOCKS: 'blocks'
}

export { useThemeSettings, THEME_SETTINGS_CONSTANTS };
/**
 * @typedef {Object} BlockConfig
 * @property {boolean} supported - whether blocks are supported
 * @property {Array<{key: string, label: string, allowedPages: string[]}>} positions - available block positions
 * @property {Object.<string, Object>} defaultLayouts - default layouts for blocks
 */

